// ============================================================================
// SEÇÃO AÇÕES SOCIAIS — HOME PAGE
// ============================================================================
// Arquivo: src/components/Home/SecaoAcoesSociais.tsx
// CREATED: 2026-07-20
// Design: Luxury cosmos + dourado, mesma linguagem visual do site
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Heart, MapPin, Calendar, ArrowRight, Users, ExternalLink } from 'lucide-react';
import { API_BASE } from '../utils/apiConfig';

interface Partner {
  name:     string;
  logoUrl?: string;
  website?: string;
}

interface SocialAction {
  id:           string;
  title:        string;
  slug:         string;
  summary:      string | null;
  coverImageUrl: string | null;
  eventDate:    string | null;
  location:     string | null;
  partners:     Partner[];
  publishedAt:  string | null;
}

const fmtDate = (d?: string | null): string => {
  if (!d) return '';
  const part = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '';
  const [y, m, day] = part.split('-');
  const meses = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  return `${parseInt(day)} ${meses[parseInt(m) - 1]}. ${y}`;
};

// ─── Card de Ação Social ──────────────────────────────────────────────────────
const ActionCard: React.FC<{ action: SocialAction; onClick: () => void }> = ({ action, onClick }) => (
  <article
    onClick={onClick}
    className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer
               border border-gray-100 hover:border-[#d4af37]/40
               shadow-md hover:shadow-2xl hover:shadow-[#d4af37]/10
               transition-all duration-500 hover:-translate-y-1 flex flex-col"
  >
    {/* Imagem */}
    <div className="relative h-52 bg-gradient-to-br from-emerald-900 to-[#0a0e27] overflow-hidden flex-shrink-0">
      {action.coverImageUrl ? (
        <img
          src={action.coverImageUrl}
          alt={action.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-90"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Heart className="w-16 h-16 text-emerald-400/40" />
        </div>
      )}
      {/* Overlay gradiente */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

      {/* Badge Ação Social */}
      <div className="absolute top-3 left-3">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold
                         bg-emerald-500/90 text-white backdrop-blur-sm">
          <Heart className="w-3 h-3" /> Ação Social
        </span>
      </div>

      {/* Data */}
      {action.eventDate && (
        <div className="absolute bottom-3 right-3">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold
                           bg-black/50 text-white backdrop-blur-sm">
            <Calendar className="w-3 h-3" />
            {fmtDate(action.eventDate)}
          </span>
        </div>
      )}
    </div>

    {/* Conteúdo */}
    <div className="p-5 flex flex-col flex-1">
      <h3 className="text-base font-black text-gray-900 mb-2 line-clamp-2 leading-snug
                     group-hover:text-emerald-700 transition-colors">
        {action.title}
      </h3>

      {action.summary && (
        <p className="text-sm text-gray-500 line-clamp-2 mb-4 flex-1 leading-relaxed">
          {action.summary}
        </p>
      )}

      {/* Local + Parceiros */}
      <div className="mt-auto space-y-1.5">
        {action.location && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            <span className="truncate">{action.location}</span>
          </div>
        )}
        {action.partners.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            <span>
              {action.partners.length === 1
                ? action.partners[0].name
                : `${action.partners[0].name} +${action.partners.length - 1}`}
            </span>
          </div>
        )}
      </div>

      {/* Seta */}
      <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-bold mt-3
                      group-hover:gap-2.5 transition-all">
        Ver detalhes <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </div>
  </article>
);

// ─── Modal de Detalhe ─────────────────────────────────────────────────────────
const ActionModal: React.FC<{ action: SocialAction; onClose: () => void }> = ({ action, onClose }) => {
  // Fecha com ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,14,39,0.85)', backdropFilter: 'blur(6px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[88vh] overflow-auto shadow-2xl
                      animate-[fadeInLuxury_0.3s_ease-out]">
        {/* Imagem hero */}
        {action.coverImageUrl && (
          <div className="relative h-64 overflow-hidden rounded-t-3xl">
            <img src={action.coverImageUrl} alt={action.title}
              className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <button onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 text-white
                         flex items-center justify-center hover:bg-black/60 transition-all text-lg font-bold">
              ×
            </button>
          </div>
        )}

        <div className="p-7 space-y-5">
          {/* Título */}
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold
                             bg-emerald-100 text-emerald-700 mb-3">
              <Heart className="w-3 h-3" /> Ação Social
            </span>
            <h2 className="text-2xl font-black text-gray-900 leading-tight">{action.title}</h2>
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap gap-3">
            {action.eventDate && (
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-600
                              bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
                <Calendar className="w-4 h-4 text-emerald-500" />
                {fmtDate(action.eventDate)}
              </div>
            )}
            {action.location && (
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-600
                              bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
                <MapPin className="w-4 h-4 text-emerald-500" />
                {action.location}
              </div>
            )}
          </div>

          {/* Descrição */}
          {action.summary && (
            <p className="text-gray-600 leading-relaxed border-l-4 border-emerald-400 pl-4 italic">
              {action.summary}
            </p>
          )}

          {/* Parceiros */}
          {action.partners.length > 0 && (
            <div>
              <h4 className="text-sm font-black text-gray-700 mb-3 flex items-center gap-2 uppercase tracking-wider">
                <Users className="w-4 h-4 text-emerald-600" /> Parceiros & Patrocinadores
              </h4>
              <div className="flex flex-wrap gap-2">
                {action.partners.map((p, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-gray-50 rounded-xl px-3 py-2.5
                                          border border-gray-200 hover:border-emerald-300 transition-all">
                    {p.logoUrl && (
                      <img src={p.logoUrl} alt={p.name} className="w-8 h-8 object-contain rounded" />
                    )}
                    <div>
                      <p className="text-sm font-bold text-gray-900">{p.name}</p>
                      {p.website && (
                        <a href={p.website} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Visitar site
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 pb-7">
          <button onClick={onClose}
            className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-bold
                       hover:bg-gray-50 transition-all">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Seção Principal ─────────────────────────────────────────────────────────
const SecaoAcoesSociais: React.FC = () => {
  const [actions, setActions]     = useState<SocialAction[]>([]);
  const [loading, setLoading]     = useState(true);
  const [selecionada, setSelecionada] = useState<SocialAction | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/social-actions/published?limit=6`);
        if (!res.ok) return;
        const data = await res.json();
        setActions(data.actions ?? []);
      } catch { /* silencioso — seção simplesmente não aparece */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Não renderiza a seção se não houver ações publicadas
  if (!loading && actions.length === 0) return null;

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decoração de fundo */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/30 to-transparent" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full
                      bg-emerald-50 opacity-60 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Cabeçalho */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full
                          bg-emerald-50 border border-emerald-200 text-emerald-700
                          text-sm font-bold mb-5">
            <Heart className="w-4 h-4" />
            Iniciativas Sociais
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">
            Ações que{' '}
            <span style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              transformam
            </span>
          </h2>

          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            A Federação vai além da certificação — promovemos ações concretas
            que impactam comunidades e fortalecem nossa categoria.
          </p>

          {/* Divisor dourado */}
          <div className="w-20 h-1 rounded-full mx-auto mt-6"
            style={{ background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                <div className="h-52 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid de cards */}
        {!loading && (
          <div className={`grid gap-6 ${
            actions.length === 1
              ? 'grid-cols-1 max-w-md mx-auto'
              : actions.length === 2
              ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto'
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {actions.map(action => (
              <ActionCard
                key={action.id}
                action={action}
                onClick={() => setSelecionada(action)}
              />
            ))}
          </div>
        )}

        {/* Rodapé da seção */}
        {!loading && actions.length > 0 && (
          <div className="text-center mt-12">
            <p className="text-sm text-gray-400 font-medium">
              Junte-se a nós nessas iniciativas e ajude a transformar vidas através da beleza.
            </p>
          </div>
        )}
      </div>

      {/* Modal de detalhe */}
      {selecionada && (
        <ActionModal
          action={selecionada}
          onClose={() => setSelecionada(null)}
        />
      )}
    </section>
  );
};

export default SecaoAcoesSociais;